import {logEventBooking} from 'main/analytics/analytics';
import {LogEventBookingEnum} from 'main/analytics/logEventEnum';
import {
	FacialBiometricEnum,
	FeaturesEnum,
	OperationStateType,
	OperationStateTypesEnum,
	TelehealthBookingEnum,
} from 'presentation/constants';
import {EntryPointCodeEnum} from 'presentation/constants/entryPointCodeEnum';
import {OperationStateErrorType} from 'presentation/constants/operationStateErrorEnum';
import {BookingTypeEnum} from 'presentation/modules/booking/shared/constants/bookingTypeEnum';
import {useBookingStore} from 'presentation/modules/booking/shared/store/useBookingStore';
import {resolveBookingError} from 'presentation/modules/booking/shared/utils/resolveBookingError';
import {useGetHomeQuery} from 'presentation/pages/private/home/query/useGetHomeQuery';
import {useMediaQueries} from 'presentation/pages/private/hooks/useMediaQueries';
import {useAccountDataStore} from 'presentation/pages/private/store/useAccountDataStateStore';
import extractTimeFromUTC from 'presentation/utils/extractTimeFromUTC';
import {useEffect, useState} from 'react';

import {TELEMEDICINE_INFO} from 'presentation/modules/booking/shared/constants/telemedicineInfo';
import {useGetHealthOpenSchedulingMutation} from 'presentation/modules/booking/shared/mutations/useGetHealthOpenSchedulingMutation';
import {usePostHealthScheduledAppointmentsMutation} from 'presentation/modules/booking/shared/mutations/usePostHealthScheduledAppointmentsMutation';
import {usePutHealthRescheduleAppointmentsMutation} from 'presentation/modules/booking/shared/mutations/usePutHealthRescheduleAppointmentsMutation';
import {HandleConfirmationParams} from './types';
import {useLocation, useNavigate} from 'react-router-dom';
import {useFacialBiometricsStore} from 'presentation/modules/booking/shared/store/useFacialBiometricsStore';
import {useFacialBiometricFeatureFlag} from 'presentation/pages/private/hooks/useFacialBiometricFeatureFlag';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';

export function useTelehealthConfirmationBooking() {
	const navigate = useNavigate();
	const {search, state} = useLocation();
	const bookingData = useBookingStore(state => state.bookingData);
	const setBookingData = useBookingStore(state => state.setBookingData);
	const [bookingState, setBookingState] = useState<OperationStateType | null>(null);
	const [typeError, setTypeError] = useState<OperationStateErrorType | null>(null);
	const {isMobile} = useMediaQueries();
	const {
		beneficiary: {cdUsuario},
		companyPortal,
	} = useAccountDataStore(state => state.accountData);

	const hasFacialBiometrics = useFacialBiometricsStore(state => state.hasFacialBiometrics);
	const registerLater = useFacialBiometricsStore(state => state.registerLater);
	const {isEnabled: isFacialBiometricEnabled} = useFacialBiometricFeatureFlag();

	const {refetch} = useGetHomeQuery();
	const {isEnabled: isHideConfirmationBookingModalEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_HIDE_CONFIRMATION_BOOKING_MODAL,
	);

	const {bookingNumber, isRebooking} = bookingData;

	const {mutateAsync: postHealthScheduledAppointmentsMutation} =
		usePostHealthScheduledAppointmentsMutation();
	const {mutateAsync: putHealthRescheduleAppointmentsMutation} =
		usePutHealthRescheduleAppointmentsMutation();
	const {mutateAsync: getHealthOpenSchedulingMutation} = useGetHealthOpenSchedulingMutation();

	const isError = bookingState === OperationStateTypesEnum.ERROR;
	const isUnauthorized = bookingState === OperationStateTypesEnum.UNAUTHORIZED;
	const isDone = bookingState === OperationStateTypesEnum.DONE;
	const isSubmitting = bookingState === OperationStateTypesEnum.SUBMITTING;
	const isUnavailable = bookingState === OperationStateTypesEnum.UNAVAILABLE;
	const isOpenScheduling = bookingState === OperationStateTypesEnum.OPEN_SCHEDULING;

	const isBookingOrReBookingError = typeError !== null && ['001', '004'].includes(typeError);
	const isHourError = typeError !== null && ['002', '003'].includes(typeError);
	const isUnexpectedError = isError && !typeError;
	const hasHourOrAvailabilityError = isHourError || isUnavailable;

	useEffect(() => {
		if (state?.from === FacialBiometricEnum.FACIAL_BIOMETRIC) {
			handleBookingAction();
		}
	}, [state]);

	const handleBookingAction = () => {
		if (isRebooking && bookingNumber) {
			return rebookTelehealth(bookingNumber);
		}
		return bookTelehealth();
	};

	const handleOpenSchedulingCheck = async () => {
		const openSchedulingData = await getHealthOpenSchedulingMutation({
			userCode: cdUsuario,
			specialtyCode: Number(bookingData.specialtyCode),
		});
		if (openSchedulingData) {
			setBookingState(OperationStateTypesEnum.OPEN_SCHEDULING);
		}
	};

	const bookTelehealth = async (skipOpenSchedulingCheck = false) => {
		if (!hasFacialBiometrics && isFacialBiometricEnabled && !registerLater) {
			navigate({pathname: FacialBiometricEnum.FACIAL_BIOMETRIC, search});
			return;
		}

		try {
			setBookingState(OperationStateTypesEnum.SUBMITTING);
			if (!skipOpenSchedulingCheck) {
				await handleOpenSchedulingCheck();
			}

			const formattedHour = extractTimeFromUTC(bookingData.hourAt);
			const formattedDate = `${bookingData.dateAt} ${formattedHour}`;

			const {content} = await postHealthScheduledAppointmentsMutation({
				specialtyCode: Number(bookingData.specialtyCode),
				subSpecialtyCode: bookingData.subSpecialtyCode
					? Number(bookingData.subSpecialtyCode)
					: undefined,
				providerCode: Number(bookingData.professionalCode),
				clinicCode: Number(TELEMEDICINE_INFO.code),
				date: formattedDate,
				entryPointCode: Number(EntryPointCodeEnum.V2),
				cellPhoneNumber: bookingData.phone,
				email: bookingData.email,
				operatorName: companyPortal?.operatorName,
				operatorEmail: companyPortal?.operatorEmail,
				operatorDocument: companyPortal?.operatorDocument,
			});

			logEventBooking({
				eventName: LogEventBookingEnum.PORTAL_TELE_BOOKING_COMPLETE,
			});

			handleConfirmOperation({
				id: content.number.toString(),
				protocolNumber: content.protocol.toString(),
			});
		} catch (error) {
			const {state, errorCode} = resolveBookingError(error);

			if (errorCode) {
				setTypeError(errorCode);
			}

			setBookingState(state);
		}
	};

	const rebookTelehealth = async (bookingNumber: string) => {
		if (!hasFacialBiometrics && isFacialBiometricEnabled && !registerLater) {
			navigate({pathname: FacialBiometricEnum.FACIAL_BIOMETRIC, search});
			return;
		}

		const formattedHour = extractTimeFromUTC(bookingData.hourAt);
		const formattedDate = `${bookingData.dateAt} ${formattedHour}`;

		try {
			setBookingState(OperationStateTypesEnum.SUBMITTING);
			const {content} = await putHealthRescheduleAppointmentsMutation({
				specialtyCode: Number(bookingData.specialtyCode),
				subSpecialtyCode: bookingData.subSpecialtyCode
					? bookingData.subSpecialtyCode
					: undefined,
				providerCode: Number(bookingData.professionalCode),
				clinicCode: Number(TELEMEDICINE_INFO.code),
				date: formattedDate,
				entryPointCode: Number(EntryPointCodeEnum.V2),
				number: Number(bookingNumber),
				email: bookingData.email,
				cellPhoneNumber: bookingData.phone,
				operatorName: companyPortal?.operatorName,
				operatorEmail: companyPortal?.operatorEmail,
				operatorDocument: companyPortal?.operatorDocument,
			});

			logEventBooking({
				eventName: LogEventBookingEnum.PORTAL_TELE_BOOKING_COMPLETE,
			});

			handleConfirmOperation({
				id: content.number.toString(),
				protocolNumber: content.protocol.toString(),
			});
		} catch (error) {
			const {state} = resolveBookingError(error);
			setBookingState(state);
		}
	};

	const handleConfirmOperation = async ({id, protocolNumber}: HandleConfirmationParams) => {
		setBookingData(prev => ({
			...prev,
			protocolNumber,
			bookingType: BookingTypeEnum.TELEHEALTH,
			bookingNumber: id,
		}));

		await refetch();

		if (isHideConfirmationBookingModalEnabled) {
			const path = TelehealthBookingEnum.TELEHEALTH_BOOKING_CONFIRMATION.replace(':id', id);

			navigate(path);
			setBookingState(null);
		} else {
			setBookingState(OperationStateTypesEnum.DONE);
		}
	};

	const proceedWithBooking = async () => {
		setBookingState(OperationStateTypesEnum.NONE);
		await bookTelehealth(true);
	};

	return {
		isMobile,
		handleBookingAction,
		isSubmitting,
		isBookingOrReBookingError,
		hasHourOrAvailabilityError,
		isUnexpectedError,
		isUnauthorized,
		isDone,
		isOpenScheduling,
		setBookingState,
		proceedWithBooking,
		setTypeError,
	};
}
